import React, { useEffect, useState } from 'react';
import { Trash2 } from 'react-feather';
import { Button, Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import PolygonMap from '../Polygon';
import { adminAPIURL } from '../../../../../../../../Config';
import axios from 'axios';
import { AsyncPaginate } from 'react-select-async-paginate';
import cogoToast from 'cogo-toast';

const AddZone = ({
  zones,
  setZones,
  errors,
  setErrors,
  activeAirport,
  distanceType,
  setDistanceType,
  isUpdate = false,
  isCreateFromOnboarding = false,
}) => {
  console.log('zones', zones);

  const handleZoneChange = (index, field, value) => {
    const updated = [...zones[distanceType]];
    updated[index][field] = value;

    // Reset error for current index if value filled
    if (value && field === 'name') {
      const errs = [...(errors[distanceType] || [])];
      errs[index] = false;
      setErrors(prev => ({ ...prev, [distanceType]: errs }));
    }

    setZones(prev => ({ ...prev, [distanceType]: updated }));
  };

  const handleRangeChange = (index, value) => {
    if (index === zones[distanceType].length - 1) {
      const updatedZones = [...zones[distanceType]];

      const minStart = index === 0 ? 0 : updatedZones[index - 1].zoneRange[1];

      // Clamp the start to prevent going back
      const clampedStart = Math.max(value[0], minStart);
      const clampedEnd = Math.max(clampedStart, value[1]);

      updatedZones[index].zoneRange = [clampedStart, clampedEnd];

      setZones(prev => ({ ...prev, [distanceType]: updatedZones }));
    }
  };

  const addZone = () => {
    const currentZones = zones[distanceType];
    const errors = currentZones.map(zone => !zone.name?.trim());

    const hasError = errors.includes(true);
    if (hasError) {
      setErrors(prev => ({ ...prev, [distanceType]: errors }));
      return;
    }

    // Check if last zone's zone range is [0, 0]
    const lastZone = currentZones[currentZones.length - 1];
    if (lastZone.zoneRange[0] === 0 && lastZone.zoneRange[1] === 0) {
      cogoToast.error('Start and End cannot both be 0.');
      return;
    }

    const lastRangeEnd = currentZones[currentZones.length - 1].zoneRange[1];
    const newStart = lastRangeEnd;
    const newEnd = lastRangeEnd + 10 <= 100 ? lastRangeEnd + 10 : 100;
    const updated = [...currentZones, { name: '', zoneRange: [newStart, newEnd] }];

    setZones(prev => ({ ...prev, [distanceType]: updated }));
    setErrors(prev => ({ ...prev, [distanceType]: [...errors, false] }));
  };

  const deleteZone = index => {
    const updatedZones = [...zones[distanceType]];
    updatedZones.splice(index, 1);
    setZones(prev => ({ ...prev, [distanceType]: updatedZones }));
  };

  const getPositionPercent = value => `${(value / 100) * 100}%`;

  /* ---------------------------- TYPED USER HANDLER ---------------------------- */
  async function loadOptionsForAirport(search) {
    const response = await axios({
      method: 'GET',
      url: `${adminAPIURL}/v2/benefit-type/airport-transfer/airports`,
      headers: {
        'Project-Code': `ADMIN_ACCESS`,
      },
      params: {
        search,
      },
      withCredentials: true,
    });

    return {
      options: response?.data?.data?.map((data, index) => {
        return {
          value: data.id,
          label: `${data?.name}`,
        };
      }),
      hasMore: false,
    };
  }

  /* ---------------------------- ON SELECT USER ---------------------------- */
  const onSelectAirport = event => {
    setZones({ ...zones, airport: event });
    setErrors(prev => ({ ...prev, airport: false }));
  };

  return (
    <Row>
      <Col md={5}>
        {distanceType !== 'Polygon' && (
          <>
            <p className="fs-14 fw-medium mb-2">Distance Type</p>
            <div className="d-flex gap-2">
              {!isUpdate ? (
                ['Road', 'Radial', 'Polygon'].map(type => (
                  <div
                    key={type}
                    style={{
                      borderRadius: '25px',
                      color: '#6F7594',
                      border: distanceType === type ? '1px solid #405189' : '1px solid #D0D2DC',
                      background: distanceType === type ? '#ABB9E8' : 'transparent',
                    }}
                    onClick={() => setDistanceType(type)}
                    className="p-2 cursor-pointer"
                  >
                    {type}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    borderRadius: '25px',
                    color: '#6F7594',
                    border: '1px solid #405189',
                    background: '#ABB9E8',
                  }}
                  className="p-2 cursor-pointer"
                >
                  {distanceType}
                </div>
              )}
            </div>
          </>
        )}
      </Col>

      {distanceType !== 'Polygon' && (
        <Col md={7}>
          <Label>Airport Name</Label>
          {isUpdate || isCreateFromOnboarding ? (
            <Input value={activeAirport?.name || ''} disabled />
          ) : (
            <>
              <AsyncPaginate
                debounceTimeout={500}
                value={zones?.airport ?? null}
                loadOptions={loadOptionsForAirport}
                onChange={e => onSelectAirport(e)}
                isMulti={false}
                closeMenuOnSelect={true}
                noOptionsMessage={() => 'No results found'}
                cacheUniqs={['code']} // force re-fetch if token changes
                placeholder="Select Airport"
              />
              {errors?.airport && <div className="text-danger">Airport is required</div>}
            </>
          )}
        </Col>
      )}

      {(distanceType === 'Road' || distanceType === 'Radial') && (
        <>
          {' '}
          <Card className="bg-light mt-4">
            <CardBody>
              <h5 className="fw-semibold">Zone Configuration</h5>
              <hr />
              {zones[distanceType].map((zone, index) => {
                return (
                  <Row className="align-items-center mb-3" key={index}>
                    <Col md={5}>
                      <Label className="mb-4">Zone Name</Label>
                      <Input
                        placeholder="Enter Zone Name"
                        value={zone.name}
                        onChange={e => handleZoneChange(index, 'name', e.target.value)}
                        invalid={errors[distanceType]?.[index]}
                      />
                      {errors[distanceType]?.[index] && <div className="text-danger">Zone name is required</div>}
                    </Col>

                    <Col md={6}>
                      <Label className="mb-4">Zone Range</Label>
                      <div className="position-relative">
                        {/* Floating labels */}
                        <div className="position-absolute w-100" style={{ top: -25 }}>
                          <div className="position-absolute" style={{ left: getPositionPercent(zone.zoneRange[0]), transform: 'translateX(-50%)' }}>
                            <span className="badge bg-primary">{zone.zoneRange[0]} KM</span>
                          </div>
                          <div className="position-absolute" style={{ left: getPositionPercent(zone.zoneRange[1]), transform: 'translateX(-50%)' }}>
                            <span className="badge bg-primary">{zone.zoneRange[1]} KM</span>
                          </div>
                        </div>

                        <Slider
                          range
                          min={0}
                          max={100}
                          value={zone.zoneRange}
                          onChange={value => handleRangeChange(index, value)}
                          trackStyle={[{ backgroundColor: '#2c3e80', height: 10 }]}
                          handleStyle={[
                            {
                              backgroundColor: 'rgb(64, 62, 128)',
                              borderColor: '#fff',
                              height: 30,
                              width: 30,
                              marginTop: -7,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                              border: '7px solid white',
                            },
                            {
                              backgroundColor: 'rgb(64, 62, 128)',
                              borderColor: '#fff',
                              height: 30,
                              width: 30,
                              marginTop: -7,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                              border: '7px solid white',
                            },
                          ]}
                          railStyle={{ backgroundColor: '#e8effc', height: 10 }}
                          allowCross={false}
                        />

                        <div className="d-flex justify-content-between mt-1">
                          <span>{0} KM</span>
                          <span>{100} KM</span>
                        </div>
                      </div>
                    </Col>

                    <Col md={1} className="d-flex align-items-end justify-content-end">
                      {zones[distanceType].length > 1 && (
                        <Button color="danger" onClick={() => deleteZone(index)} className="mt-3">
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </Col>
                  </Row>
                );
              })}

              {!isUpdate && (
                <div className="text-end">
                  <Button color="link" onClick={addZone}>
                    + Add New
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>{' '}
        </>
      )}

      {distanceType === 'Polygon' && (
        <Row>
          <Col md={4} className="mt-3">
            <p className="fs-14 fw-medium">Distance Type</p>
            <Col className="d-flex gap-2" md={12}>
              {!isUpdate ? (
                ['Road', 'Radial', 'Polygon'].map(type => (
                  <div
                    key={type}
                    style={{
                      borderRadius: '25px',
                      color: '#6F7594',
                      border: distanceType === type ? '1px solid #405189' : '1px solid #D0D2DC',
                      background: distanceType === type ? '#ABB9E8' : 'transparent',
                    }}
                    onClick={() => setDistanceType(type)}
                    className="p-2 cursor-pointer mb-3"
                  >
                    {type}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    borderRadius: '25px',
                    color: '#6F7594',
                    border: '1px solid #405189',
                    background: '#ABB9E8',
                  }}
                  className="p-2 cursor-pointer"
                >
                  {distanceType}
                </div>
              )}
            </Col>
            <Col md={12} className="mb-2">
              <Label>Airport Name</Label>
              {isUpdate || isCreateFromOnboarding ? (
                <Input value={activeAirport?.name || ''} disabled />
              ) : (
                <>
                  <AsyncPaginate
                    debounceTimeout={500}
                    value={zones?.airport ?? null}
                    loadOptions={loadOptionsForAirport}
                    onChange={e => onSelectAirport(e)}
                    isMulti={false}
                    closeMenuOnSelect={true}
                    noOptionsMessage={() => 'No results found'}
                    cacheUniqs={['code']} // force re-fetch if token changes
                    placeholder="Select Airport"
                  />
                  {errors?.airport && <div className="text-danger">Airport is required</div>}
                </>
              )}
            </Col>
            <Col md={12} className="mb-2">
              <Label>Zone Name</Label>
              <Input
                placeholder="Enter Zone Name"
                value={zones[distanceType]?.name}
                onChange={e => {
                  setZones(prev => ({
                    ...prev,
                    Polygon: {
                      ...prev?.Polygon,
                      name: e?.target?.value,
                    },
                  }));
                  setErrors(err => ({ ...err, Polygon: { ...err?.Polygon, name: false } }));
                }}
              />
              {errors[distanceType]?.name && <div className="text-danger">Zone name is required</div>}
            </Col>
            <Col md={12} className="mb-2">
              <Label>Servicable Zone</Label>
              <textarea
                className="form-control"
                placeholder="Paste valid GeoJSON here"
                rows={8}
                value={zones[distanceType]?.serviceableAreas}
                onChange={e => {
                  setZones(prev => ({
                    ...prev,
                    Polygon: {
                      ...prev?.Polygon,
                      serviceableAreas: e?.target?.value,
                    },
                  }));
                  setErrors(err => ({ ...err, Polygon: { ...err?.Polygon, serviceableAreas: false } }));
                }}
                style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
              />
              {errors[distanceType]?.serviceableAreas && !zones[distanceType]?.serviceableAreas && (
                <div className="text-danger">Polygon is required</div>
              )}
              {errors[distanceType]?.serviceableAreas && zones[distanceType]?.serviceableAreas && (
                <div className="text-danger">Incorrect polygon format</div>
              )}
            </Col>
            <Col md={12} className="mb-2">
              <Label>Non Servicable Zone</Label>
              <textarea
                className="form-control"
                placeholder="Paste valid GeoJSON here"
                rows={8}
                value={zones[distanceType]?.nonServiceableAreas}
                onChange={e => {
                  setZones(prev => ({
                    ...prev,
                    Polygon: {
                      ...prev?.Polygon,
                      nonServiceableAreas: e?.target?.value,
                    },
                  }));
                  setErrors(err => ({ ...err, Polygon: { ...err?.Polygon, nonServiceableAreas: false } }));
                }}
                style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
              />
              {errors[distanceType]?.nonServiceableAreas && !zones[distanceType]?.nonServiceableAreas && (
                <div className="text-danger">Polygon is required</div>
              )}
              {errors[distanceType]?.nonServiceableAreas && zones[distanceType]?.nonServiceableAreas && (
                <div className="text-danger">Incorrect polygon format</div>
              )}
            </Col>
          </Col>
          <Col md={8}>
            <PolygonMap serviceableAreas={zones[distanceType]?.serviceableAreas} nonServiceableAreas={zones[distanceType]?.nonServiceableAreas} />
          </Col>
        </Row>
      )}
    </Row>
  );
};

export default AddZone;
